import { FC, useEffect, useMemo } from 'react';
import { InlineNotification } from '@carbon/react';
import { subscribe } from 'lib/pusher';
import { VestingDocumentCard } from 'components/vesting-documents/vesting-document-card';
import type { VestingDocumentRow } from 'components/vesting-documents/types';
import { EXTRACTION_STATUS_EVENT } from 'components/vesting-documents/atoms';
import { useVestingDocumentActions } from 'components/vesting-documents/vesting-document-actions';
import type { RelatedItem } from './types';
import styles from './related-items.module.scss';

// The expanded body of a contributed vesting-document row: the card, not a field grid.
//
// Self-contained on purpose. RelatedItemDetail knows only that this kind renders a component, and
// this component owns everything vesting-specific — the two actions, their errors, and the wait
// for a freshly uploaded deed to finish extracting. Threading verify/remove handlers down through
// the section for one kind would put vesting knowledge in code that serves nine others.

interface Props {
  /** Only the card payload is read. */
  document: Pick<RelatedItem, 'details'>;
  /** Hides both actions. Passed explicitly rather than inferred from `onChanged` being absent:
   *  those coincide today, but "cannot refresh" and "may not write" are different claims, and
   *  conflating them makes a row writable the moment a read-only section wants a refresh. */
  readOnly?: boolean;
  /** Ask the section to re-read its list, so a verification or a removal is reflected in the row. */
  onChanged?: () => void;
  /** Replaces the direct verify, e.g. a draft workspace staging it until promote. */
  verification?: {
    staged: boolean;
    verifying: boolean;
    onVerify: () => void;
    onUndo: () => void;
  };
}

/**
 * The card payload carried on a contributed vesting row.
 *
 * `details` is typed Record<string, unknown> because it is a per-kind snapshot echoed verbatim, so
 * reading it is a genuine boundary rather than a cast we can type away. Checking the fields the
 * card actually dereferences means a malformed payload renders nothing instead of throwing inside
 * the card — `details` being present is not the same as it being this shape.
 * @param {Pick<RelatedItem, 'details'>} doc - The contributed row.
 * @return {VestingDocumentRow | undefined} The payload, or undefined when it is not one.
 */
const vestingRow = (
  doc: Pick<RelatedItem, 'details'>
): VestingDocumentRow | undefined => {
  const details = doc.details as Partial<VestingDocumentRow> | null | undefined;
  if (!details?.href || !details.document) return undefined;

  return details as VestingDocumentRow;
};

export const VestingDocumentDetail: FC<Props> = ({
  document: doc,
  readOnly = false,
  onChanged,
  verification,
}) => {
  const row = vestingRow(doc);

  const { error, verify, verifyingId, askRemove, confirmation } =
    useVestingDocumentActions(onChanged);

  // A deed still being read has no citation yet, and extraction lands minutes later. Its own
  // channel already carries the transition — see the notifications block on the document payload.
  const channel = useMemo(() => {
    if (row?.documentState !== 'processing') return undefined;

    return row.document.notifications?.channel;
  }, [row?.documentState, row?.document.notifications?.channel]);

  useEffect(() => {
    if (!channel || !onChanged) return () => void 0;

    const subscription = subscribe(channel);
    subscription.bind(EXTRACTION_STATUS_EVENT, onChanged);
    // Unbind only our handler — the channel is the document's and is shared with any other
    // surface watching it; unsubscribe() would drop their bindings too.
    return () => subscription.unbind(EXTRACTION_STATUS_EVENT, onChanged);
  }, [channel, onChanged]);

  // A contributed row always carries its payload; this guards the type boundary rather than a
  // state the backend produces.
  if (!row) return null;

  return (
    <div className={styles.detail}>
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title=""
          subtitle={error}
        />
      )}
      <VestingDocumentCard
        row={row}
        readOnly={readOnly}
        verifying={verification?.verifying ?? verifyingId === row.id}
        onVerify={verification?.onVerify ?? (() => verify(row))}
        onRemove={() => askRemove(row)}
        staged={verification?.staged}
        onUndoVerify={verification?.onUndo}
      />
      {confirmation}
    </div>
  );
};
